import {NgClass} from '@angular/common';
import {Component, Injectable, input, OnChanges, output, signal, SimpleChanges} from '@angular/core';
import {BytesConverterPipe} from '../../pipes/bytes-converter.pipe';
import {Button} from '../button/button.component';
import {Icon} from '../icon/icon.component';

/**
 * Item que deve ser exibido pelo componente FilePreview.
 *
 */
export type FilePreviewItem = {
    id: string;
    file: File | undefined;
    name: string | undefined;
    mimeType: string | undefined;
    size: number | undefined;
    encoded: string;
    loading: boolean | undefined;
    error: boolean | undefined;
    processing: boolean | undefined;
};

@Injectable({providedIn: 'root'})
export class FilePreviewItemFactory {
    public create(file: File): FilePreviewItem {
        return {
            file: file,
            name: file.name,
            size: file.size,
            mimeType: file.type,
            encoded: "",
            loading: false,
            error: false,
            processing: false,
        } as FilePreviewItem;
    }
}

/**
 * Evento que deve ser lançado quando for acionada a ação
 * de remover o arquivo representado pelo Preview.
 *
 */
export type FilePreviewDeleteEvent = {
    file: File | FilePreviewItem | undefined;
    event: Event;
};

/**
 * Evento que deve ser lançado quando for acionada a ação
 * de retry.
 */
export type FilePreviewRetryEvent = {
    file: File | FilePreviewItem | undefined;
    event: Event;
};

/**
 * Define um mapeamento de um tipo para uma imagem que deve ser exibida
 * pelo componente.
 *
 */
export type FilePreviewTypeIconMapping = {
    mimetype: string;
    image?: string;
};

/**
 * Lista padrão de mapeamentos.
 *
 */
export const DefaultFilePreviewTypeIconMapping: FilePreviewTypeIconMapping[] = [
    {
        mimetype: 'application/pdf',
        image: 'assets/images/file-preview/pdf.svg',
    },
    {
        mimetype: 'image/png',
        image: 'assets/images/file-preview/png.svg',
    },
    {
        mimetype: 'image/jpeg',
        image: 'assets/images/file-preview/jpg.svg',
    },
    {
        mimetype:
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        image: 'assets/images/file-preview/docx.svg',
    },
    {
        mimetype: 'application/msword',
        image: 'assets/images/file-preview/docx.svg',
    },
    {
        mimetype:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        image: 'assets/images/file-preview/xlsx.svg',
    },
    {
        mimetype:
            'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        image: 'assets/images/file-preview/pptx.svg',
    },
    {
        mimetype: '*/*',
        image: 'assets/images/file-preview/unknown-file-type.svg',
    },
    {
        mimetype: 'application/xml',
        image: 'assets/images/file-preview/xml.svg',
    },
    {
        mimetype: 'text/html',
        image: 'assets/images/file-preview/html.svg',
    },
    {
        mimetype: 'application/xml',
        image: 'assets/images/file-preview/xml.svg',
    },
    {
        mimetype: 'image/svg+xml',
        image: 'assets/images/file-preview/svg.svg',
    },
    {
        mimetype: 'text/plain',
        image: 'assets/images/file-preview/txt.svg',
    },
    {
        mimetype: 'text/csv',
        image: 'assets/images/file-preview/csv.svg',
    },
    {
        mimetype: 'application/json',
        image: 'assets/images/file-preview/json.svg',
    },
    {
        mimetype: 'text/markdown',
        image: 'assets/images/file-preview/md.svg',
    },
];

const IMAGE_TYPES = [
    'image/svg+xml',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/bmp',
    'image/tiff',
];

/**
 * Mapeamento de extensão de arquivo para mimetype canônico, aplicado sempre
 * que a extensão do arquivo for uma destas — independentemente do mimetype
 * informado (tipicamente vindo de `File.type`, detectado pelo navegador/SO).
 *
 * Necessário porque `.csv`, `.json`, `.md` e `.txt` não têm uma associação de
 * mimetype confiável e consistente entre navegadores/sistemas operacionais
 * (ex.: Windows com Excel instalado costuma reportar `.csv` como
 * `application/vnd.ms-excel`; `.md` frequentemente não tem associação
 * alguma e chega como string vazia). Mesma estratégia de desambiguação por
 * extensão já aplicada no backend (`CreateAttachmentUseCase._resolve_by_extension`).
 * Os demais tipos (PDF, DOCX, XLSX, imagens etc.) têm mimetype padronizado e
 * confiável entre plataformas, por isso não entram neste mapeamento.
 */
const EXTENSION_TO_MIMETYPE: Readonly<Record<string, string>> = {
    '.csv': 'text/csv',
    '.json': 'application/json',
    '.md': 'text/markdown',
    '.txt': 'text/plain',
};

/**
 * Resolve o mimetype de um arquivo: se a extensão do nome do arquivo for uma
 * das conhecidamente ambíguas (ver {@link EXTENSION_TO_MIMETYPE}), usa o
 * mimetype canônico correspondente; caso contrário, mantém o mimetype
 * original informado.
 *
 * @param name Nome do arquivo (usado para extrair a extensão).
 * @param mimeType Mimetype original, tipicamente vindo de `File.type`.
 */
function resolveMimeTypeByExtension(
    name: string | undefined,
    mimeType: string | undefined,
): string | undefined {
    if (!name || !name.includes('.')) {
        return mimeType;
    }

    const extension = `.${name.split('.').pop()!.toLowerCase()}`;

    return EXTENSION_TO_MIMETYPE[extension] ?? mimeType;
}

/**
 * Prévia de um arquivo (ícone por tipo MIME ou thumbnail de imagem), com ações
 * de remover e tentar novamente.
 *
 * Aceita um {@link File} ou um {@link FilePreviewItem}. Emite {@link delete} ao
 * remover e {@link retry} ao reprocessar.
 */
@Component({
    selector: 'ai-file-preview',
    standalone: true,
    templateUrl: './file-preview.component.html',
    styleUrls: ['./file-preview.component.scss'],
    imports: [BytesConverterPipe, NgClass, Button, Icon],
})
export class FilePreview implements OnChanges {
    protected fileItemPreview = signal<FilePreviewItem | undefined>(undefined);
    /** Define se a ação de remover está disponível. */
    public deletable = input<boolean>(true);
    /** Arquivo (ou item) a ser pré-visualizado (obrigatório). */
    public file = input.required<File | FilePreviewItem>();
    /** Desabilita as ações. */
    public disabled = input<boolean>(false);
    /** Exibe miniatura para arquivos de imagem (em vez do ícone do tipo). */
    public showImageThumbnail = input<boolean>(false);
    /** Emite ao acionar a remoção do arquivo. */
    public delete = output<FilePreviewDeleteEvent>();
    /** Emite ao acionar o reprocessamento (retry). */
    public retry = output<FilePreviewRetryEvent>();
    /** Mapeamento de tipo MIME para ícone exibido. */
    public typeIconMapping = input<FilePreviewTypeIconMapping[]>(
        DefaultFilePreviewTypeIconMapping,
    );

    public async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (changes['file'] && changes['file'].currentValue) {
            await this.handleItem(changes['file'].currentValue);
            this.handleImageFile();
        }
    }

    private async handleItem(fileValue: File | FilePreviewItem): Promise<void> {
        if (fileValue instanceof File) {
            this.fileItemPreview.set({
                id: await fileValue.toSHA256Hash(),
                file: fileValue,
                name: fileValue.name,
                mimeType: resolveMimeTypeByExtension(fileValue.name, fileValue.type),
                size: fileValue.size,
                encoded: '',
                loading: false,
                error: false,
                processing: false
            });
        } else {
            this.fileItemPreview.set({
                ...fileValue,
                mimeType: resolveMimeTypeByExtension(fileValue.name, fileValue.mimeType),
            });
        }
    }

    private handleImageFile(): void {
        let physicalFile: File | undefined;
        let type = 'application/pdf';
        let item: FilePreviewItem | undefined = undefined;

        if (this.file() instanceof File) {
            type = (this.file() as File).type;
            physicalFile = this.file() as File;
        } else {
            type = (this.file() as FilePreviewItem).mimeType!;
            physicalFile = (this.file() as FilePreviewItem).file;
            item = this.file() as FilePreviewItem;
        }

        const typeMapping = this.typeIconMapping().find(
            (mapping) => mapping.mimetype === type,
        );

        const isImage = IMAGE_TYPES.includes(type);

        // Mostrar o ícone/imagem apenas dos formatos suportados pelo componente.
        if (typeMapping) {
            // Se é uma imagem e a opção de mostrar thumbnail está ativada, então deve-se mostrar
            // o thumbnail ao invés do ícone que representa o mimetype.
            if (isImage && this.showImageThumbnail()) {
                // Exibir thumbnail faz sentido apenas se temos um File ou
                // um "encoded" contendo a URL encoded do Base64 da imagem.
                if (physicalFile) {
                    const reader = new FileReader();

                    reader.onload = (e: any) => {
                        //this.imgSrc.set(e.target.result);
                    };

                    reader.readAsDataURL(physicalFile!);
                } else if (item && item.encoded) {
                    //this.imgSrc.set(item.encoded);
                } else {
                    if (typeMapping.image) {
                        // this.imgSrc.set(typeMapping.image);
                    }
                }
            } else {
                if (typeMapping.image) {
                    //this.imgSrc.set(typeMapping.image);
                }
            }
        }
    }

    /**
     * Trata o evento lançado quando o botão de deletar é acionado.
     *
     * @param event Evento.
     */
    protected async onDeleteButtonClicked(event: Event): Promise<void> {
        this.delete.emit({
            file: this.file(),
            event: event,
        });
    }

    /**
     * Trata o evento lançado quando o botão retry é acionado.
     *
     * @param event Evento.
     * @protected
     */
    protected async onRetryButtonClicked(event: Event): Promise<void> {
        this.retry.emit({
            file: this.file(),
            event: event
        });
    }

}
